import type { Run } from "../api/types";

/** The graph, in execution order. A run reports each stage as it finishes, so
 *  the first stage without a report is the one currently working. */
const NODES: { key: string; label: string }[] = [
  { key: "plan", label: "Plan" },
  { key: "collect", label: "Sammeln" },
  { key: "enrich", label: "Anreichern" },
  { key: "extract", label: "Extrahieren" },
  { key: "match", label: "Abgleichen" },
  { key: "verify", label: "Prüfen" },
  { key: "score", label: "Bewerten" },
];

/**
 * That a search is running, where it stands, and a way out of it.
 *
 * It sits in the assistant's header rather than above the list: that strip is
 * already the place one looks for the state of things, and a run is something
 * one watches out of the corner of an eye.
 */
export function RunProgress({
  run,
  onStop,
  stopping,
}: {
  run: Run;
  onStop?: () => void;
  stopping?: boolean;
}) {
  const done = new Set(run.steps.map((step) => step.node));
  const index = NODES.findIndex((node) => !done.has(node.key));
  const stage = NODES[index === -1 ? NODES.length - 1 : index] ?? NODES[0]!;
  const percent = Math.round((done.size / NODES.length) * 100);
  const cancelled = Boolean(run.cancel_requested_at) || stopping;

  return (
    <span className="run-chip" role="status" aria-live="polite">
      <span className={"run-pulse" + (cancelled ? " stopping" : "")} aria-hidden="true" />
      <span className="run-chip-label">
        {cancelled ? "Wird beendet" : `Suchlauf · ${stage.label}`}
      </span>
      <span className="run-bar" aria-hidden="true">
        <span className="run-bar-fill" style={{ width: `${percent}%` }} />
      </span>
      {onStop && !cancelled && (
        <button
          type="button"
          className="run-stop"
          onClick={onStop}
          title="Suchlauf beenden — der Lauf hält am nächsten sicheren Punkt an"
        >
          Stoppen
        </button>
      )}
    </span>
  );
}
